export interface FAQ {
  question: string;
  answer: string;
}

export const serviceFAQs: Record<string, FAQ[]> = {
  "ux-ui": [
    {
      question: "What makes your UX/UI design approach different from competitors?",
      answer: "We combine data-driven insights with human-centered design principles. Unlike generic design agencies, we conduct thorough user research, create detailed user personas, and validate designs through A/B testing. Our designs are built for conversion, not just aesthetics."
    },
    {
      question: "How long does a typical UX/UI design project take?",
      answer: "Most projects range from 4-12 weeks depending on complexity. We work in iterative sprints, delivering wireframes in week 1-2, high-fidelity designs by week 3-4, and final assets with documentation by project completion. Rush timelines available for urgent launches."
    },
    {
      question: "Do you provide ongoing design support after project completion?",
      answer: "Yes! We offer maintenance packages including design updates, new feature designs, and performance optimization. Most clients retain us for quarterly design reviews and continuous improvement based on user feedback and analytics."
    },
    {
      question: "What's included in your design deliverables?",
      answer: "Complete design system, high-fidelity mockups, interactive prototypes, component library, design specifications, and developer handoff documentation. We also provide style guides, asset exports, and 30 days of revision support."
    },
    {
      question: "How do you ensure designs will convert visitors into customers?",
      answer: "We use conversion-focused design principles: clear value propositions, strategic CTAs, trust signals, and user flow optimization. Every design decision is backed by UX research and tested against conversion benchmarks from your industry."
    }
  ],
  "web-development": [
    {
      question: "What technologies do you use that set you apart?",
      answer: "We specialize in modern, performance-optimized stacks: React/Next.js for frontend, Node.js for backend, and cloud deployment on AWS/Vercel. Unlike competitors using outdated WordPress, our custom solutions are 3x faster and infinitely more scalable."
    },
    {
      question: "How do you ensure my website will rank well on Google?",
      answer: "We build with SEO-first architecture: semantic HTML, optimized Core Web Vitals, structured data, and technical SEO best practices. Our sites consistently achieve 90+ Google PageSpeed scores and rank higher than WordPress-based competitors."
    },
    {
      question: "What ongoing support do you provide after launch?",
      answer: "24/7 monitoring, security updates, performance optimization, and content management support. We also provide analytics reporting, conversion optimization, and feature additions as your business grows."
    },
    {
      question: "How long does website development typically take?",
      answer: "Simple sites: 2-4 weeks, complex applications: 6-12 weeks. We work in agile sprints with weekly demos, ensuring you see progress and can provide feedback throughout development. Emergency launches possible in 1-2 weeks."
    },
    {
      question: "Do you provide hosting and domain management?",
      answer: "Yes, we handle everything: domain registration, SSL certificates, CDN setup, and managed hosting on enterprise-grade infrastructure. Your site will be fast, secure, and automatically backed up with 99.9% uptime guarantee."
    }
  ],
  "ai-agents": [
    {
      question: "How are your AI agents different from ChatGPT or generic chatbots?",
      answer: "Our AI agents are custom-trained on your business data, integrate with your existing systems, and can take actions (schedule appointments, process orders, update CRMs). Unlike generic chatbots, they understand your industry nuances and provide personalized, contextual responses."
    },
    {
      question: "What ROI can I expect from implementing AI agents?",
      answer: "Clients typically see 40-60% reduction in support costs, 24/7 availability increasing lead capture by 35%, and 3x faster response times. Most implementations pay for themselves within 3-6 months through operational savings and increased conversions."
    },
    {
      question: "How do you ensure AI agents understand my specific business?",
      answer: "We train agents on your knowledge base, FAQ history, product catalogs, and brand voice. The training process includes industry-specific scenarios, compliance requirements, and edge case handling that generic solutions miss."
    },
    {
      question: "Can AI agents integrate with my existing tools (CRM, email, etc.)?",
      answer: "Absolutely. We connect AI agents to 100+ platforms including Salesforce, HubSpot, Shopify, Stripe, Calendly, and custom APIs. Agents can read data, update records, and trigger workflows across your entire tech stack."
    },
    {
      question: "What happens if the AI agent doesn't know how to handle a query?",
      answer: "Smart escalation system: agents recognize complex queries and seamlessly transfer to human agents with full conversation context. We also implement continuous learning, so agents get smarter from every interaction."
    }
  ],
  "analytics": [
    {
      question: "How is your analytics setup different from basic Google Analytics?",
      answer: "We implement advanced tracking: custom events, conversion funnels, cohort analysis, and predictive modeling. Our dashboards provide actionable insights specific to your industry, not just vanity metrics that most agencies focus on."
    },
    {
      question: "What specific metrics will help me grow my business?",
      answer: "We focus on revenue-driving metrics: customer acquisition cost (CAC), lifetime value (LTV), conversion rates by traffic source, and churn prediction. You'll see exactly which marketing channels and website changes generate the most profit."
    },
    {
      question: "How quickly will I see results from analytics implementation?",
      answer: "Data collection starts immediately, meaningful insights within 2-4 weeks, and optimization recommendations by month 2. Most clients see 15-25% improvement in conversion rates within 90 days of implementing our recommendations."
    },
    {
      question: "Do you provide ongoing analysis and recommendations?",
      answer: "Yes, monthly analytics reviews with actionable recommendations, quarterly deep-dives into performance trends, and immediate alerts for significant changes. We proactively identify opportunities for improvement and growth."
    },
    {
      question: "Can you track ROI from specific marketing campaigns?",
      answer: "Absolutely. We implement multi-touch attribution, UTM parameter tracking, and revenue attribution across all channels. You'll know exactly which ads, emails, and content pieces generate the most profitable customers."
    }
  ],
  "cro-testing": [
    {
      question: "How do you ensure CRO testing doesn't disrupt my current sales?",
      answer: "We use statistical significance testing and run experiments on small traffic percentages initially. Our testing methodology ensures minimal risk while maximizing learning. Most tests show positive results within 2-4 weeks without impacting your existing conversions."
    },
    {
      question: "What's the typical ROI from conversion rate optimization?",
      answer: "Clients typically see 15-40% improvement in conversion rates within 90 days. A 2% conversion rate becoming 2.8% means 40% more revenue from the same traffic. Most CRO investments pay for themselves within the first successful test."
    },
    {
      question: "How do you decide what elements to test first?",
      answer: "We start with high-impact, low-effort tests based on conversion audit findings: headlines, CTAs, forms, and trust signals. Our data-driven approach prioritizes tests by potential revenue impact and implementation complexity."
    },
    {
      question: "Do you test on mobile and desktop separately?",
      answer: "Absolutely. Mobile and desktop users behave differently, so we create device-specific hypotheses and tests. We also test across different traffic sources, as paid traffic often converts differently than organic visitors."
    },
    {
      question: "How long should I run CRO tests to get reliable results?",
      answer: "Minimum 2 weeks or 1000 conversions per variant, whichever comes first. We use advanced statistical methods to determine when results are significant and actionable. Running tests too short leads to false conclusions."
    }
  ],
  "ppc-marketing": [
    {
      question: "How do you reduce my cost per acquisition compared to other agencies?",
      answer: "We focus on high-intent keywords, advanced audience targeting, and continuous bid optimization. Our landing page alignment strategies and negative keyword research typically reduce CPA by 25-40% within the first 60 days."
    },
    {
      question: "What platforms do you advertise on beyond Google Ads?",
      answer: "Google Ads, Microsoft Ads, Facebook, Instagram, LinkedIn, and YouTube. We recommend platform mix based on your audience behavior and industry. B2B clients often see best ROI on LinkedIn and Google, while B2C thrives on Facebook and Google."
    },
    {
      question: "How quickly will I see results from PPC campaigns?",
      answer: "Initial data within 7 days, optimization improvements by week 3-4, and significant ROI improvements by month 2. Unlike SEO, PPC provides immediate visibility, but optimization takes time to maximize performance."
    },
    {
      question: "Do you provide transparent reporting on ad spend and performance?",
      answer: "Complete transparency with real-time dashboard access, monthly performance reports, and detailed ROI analysis. You'll see exactly where every dollar is spent and which campaigns drive the most profitable customers."
    },
    {
      question: "How do you ensure my ads stay competitive in my industry?",
      answer: "Continuous competitor analysis, bid strategy optimization, and creative testing. We monitor competitor ad copy, landing pages, and bidding patterns to maintain your competitive advantage and market share."
    }
  ],
  "lead-generation": [
    {
      question: "How do you generate leads that actually convert into paying customers?",
      answer: "We focus on lead quality over quantity using advanced targeting, lead scoring, and nurture sequences. Our multi-touch attribution ensures leads are sales-ready, with typical lead-to-customer conversion rates 3x higher than industry averages."
    },
    {
      question: "What's your average cost per qualified lead?",
      answer: "Varies by industry, but we typically reduce CPL by 30-50% compared to previous campaigns while improving lead quality. B2B services average $50-200 per qualified lead, while B2C typically ranges $10-50, depending on lifetime value."
    },
    {
      question: "How do you nurture leads until they're ready to buy?",
      answer: "Automated email sequences, retargeting campaigns, and progressive profiling to build trust over time. Our nurture campaigns typically convert 15-25% of leads within 6 months, compared to 2-3% from single-touch campaigns."
    },
    {
      question: "Can you integrate with our existing CRM and sales process?",
      answer: "Yes, we integrate with Salesforce, HubSpot, Pipedrive, and 50+ other CRMs. Leads flow automatically with lead scoring, source attribution, and behavioral data to help your sales team prioritize and close more deals."
    },
    {
      question: "How do you prevent fake or low-quality leads?",
      answer: "Multi-layer validation: email verification, IP filtering, behavioral analysis, and manual qualification processes. We also implement lead scoring to prioritize high-intent prospects and filter out unqualified traffic."
    }
  ],
  "ecommerce": [
    {
      question: "How do you increase e-commerce sales beyond just driving more traffic?",
      answer: "We optimize the entire customer journey: product page conversions, checkout flow, cart abandonment recovery, and post-purchase upsells. Most clients see 25-50% revenue increase through conversion optimization alone."
    },
    {
      question: "What e-commerce platforms do you specialize in?",
      answer: "Shopify, WooCommerce, Magento, and custom solutions. We also integrate with major tools like Klaviyo, ReCharge, Yotpo, and shipping providers to create seamless e-commerce experiences that convert browsers into buyers."
    },
    {
      question: "How do you handle seasonal fluctuations in e-commerce?",
      answer: "Strategic campaign planning around peak seasons, inventory-based bidding, and seasonal creative optimization. We prepare 60-90 days in advance for major shopping periods to maximize your share of seasonal revenue."
    },
    {
      question: "Can you help with product feed optimization and shopping campaigns?",
      answer: "Absolutely. We optimize product titles, descriptions, images, and feed data for maximum visibility. Our shopping campaign management typically increases shopping revenue by 40-70% through better product targeting and bid optimization."
    },
    {
      question: "How do you track and improve customer lifetime value?",
      answer: "Advanced analytics tracking purchase behavior, repeat purchase campaigns, and loyalty program optimization. We focus on increasing both average order value and purchase frequency to maximize long-term customer profitability."
    }
  ],
  "workflow-automation": [
    {
      question: "How much time can automation actually save my team?",
      answer: "Most clients save 15-25 hours per week through automation of repetitive tasks like data entry, follow-ups, and reporting. ROI typically shows within 30 days as staff focus on high-value activities instead of manual processes."
    },
    {
      question: "What business processes work best for automation?",
      answer: "Lead routing, email sequences, data synchronization, invoice processing, appointment scheduling, and customer onboarding. We start with your most time-consuming, repetitive tasks that follow predictable rules."
    },
    {
      question: "How do you ensure automations don't break when we update our systems?",
      answer: "Robust error handling, backup workflows, and regular monitoring with automatic alerts. We also provide maintenance packages and version our automations so updates to your systems don't disrupt critical business processes."
    },
    {
      question: "Can you automate complex workflows that involve multiple departments?",
      answer: "Yes, we specialize in cross-departmental automation connecting sales, marketing, accounting, and operations. Our workflows can route tasks, trigger approvals, and synchronize data across your entire organization."
    },
    {
      question: "How do you measure the ROI of workflow automation?",
      answer: "We track time saved, error reduction, and process completion rates before and after implementation. Most automations show 300-500% ROI within 6 months through operational efficiency gains and reduced manual errors."
    }
  ],
  "data-integration": [
    {
      question: "How do you ensure data security during integration processes?",
      answer: "Enterprise-grade encryption, secure API connections, and compliance with GDPR, SOC 2, and industry standards. All data transfers use encrypted channels with audit trails, and we never store sensitive data unnecessarily."
    },
    {
      question: "What happens if one of our integrated systems goes down?",
      answer: "Built-in redundancy with automatic failover, data queuing, and retry mechanisms. If a system is temporarily unavailable, data is safely queued and synchronized once connectivity is restored, preventing any data loss."
    },
    {
      question: "How long does a typical data integration project take?",
      answer: "Simple integrations: 1-2 weeks, complex enterprise integrations: 4-8 weeks. We work in phases with weekly checkpoints to ensure integrations work properly before moving to the next system or data source."
    },
    {
      question: "Can you handle real-time data synchronization?",
      answer: "Yes, we implement real-time sync for critical data flows like inventory updates, customer records, and financial transactions. We also offer scheduled sync for less time-sensitive data to optimize system performance."
    },
    {
      question: "How do you handle data conflicts between different systems?",
      answer: "Master data management with conflict resolution rules, data validation, and human approval workflows for edge cases. We establish data hierarchy and business rules to automatically resolve most conflicts while escalating complex cases."
    }
  ],
  "seo": [
    {
      question: "How long does it take to see SEO results?",
      answer: "Initial improvements typically show within 3-6 months, with significant ranking gains by month 6-12. Unlike PPC, SEO builds long-term organic visibility that compounds over time, providing sustainable growth and reducing dependency on paid advertising."
    },
    {
      question: "What makes your SEO approach different from other agencies?",
      answer: "We focus on technical excellence and user experience, not just keywords. Our AI-powered content optimization, Core Web Vitals optimization, and data-driven approach consistently outperform traditional SEO methods by 40-60%."
    },
    {
      question: "Do you guarantee first page rankings?",
      answer: "We don't make unrealistic guarantees, but we do provide measurable results. Our clients typically see 200-400% increases in organic traffic and 50-80% of target keywords ranking in top 10 within 12 months."
    },
    {
      question: "How do you handle Google algorithm updates?",
      answer: "We stay ahead of algorithm changes through continuous monitoring and white-hat optimization techniques. Our strategies focus on long-term sustainability, so our clients actually benefit from most Google updates rather than getting penalized."
    },
    {
      question: "Can you work with our existing website or do we need a rebuild?",
      answer: "We optimize existing websites and only recommend rebuilds when absolutely necessary for performance. Most SEO improvements can be implemented on your current site through technical optimization, content enhancement, and structural improvements."
    }
  ],
  "email-marketing": [
    {
      question: "What open rates and click-through rates should I expect?",
      answer: "Our campaigns typically achieve 25-45% open rates and 4-8% click-through rates, significantly higher than industry averages of 18-22% and 2.6%. Results vary by industry, but most clients see 40-70% improvement within 90 days."
    },
    {
      question: "How do you ensure emails don't end up in spam folders?",
      answer: "Advanced deliverability optimization including domain authentication (SPF, DKIM, DMARC), reputation management, list hygiene, and content optimization. We maintain 95%+ inbox placement rates across all major email providers."
    },
    {
      question: "Can you integrate with our existing CRM and marketing tools?",
      answer: "Yes, we integrate with 100+ platforms including Salesforce, HubSpot, Shopify, and custom APIs. Our automation workflows sync customer data, behavioral triggers, and campaign performance across your entire marketing stack."
    },
    {
      question: "How do you segment audiences for better targeting?",
      answer: "Advanced segmentation using behavioral data, purchase history, engagement levels, and predictive analytics. Our AI-powered segmentation typically improves campaign performance by 35-50% compared to basic demographic targeting."
    },
    {
      question: "What's the ROI on email marketing campaigns?",
      answer: "Email marketing typically delivers $36-42 ROI for every dollar spent. Our optimized campaigns often exceed industry averages, with clients seeing 300-500% ROI through strategic automation, personalization, and conversion optimization."
    }
  ],
  "nocode-development": [
    {
      question: "How quickly can you build and deploy a NoCode solution?",
      answer: "Simple applications: 1-2 weeks, complex enterprise solutions: 4-8 weeks. This is 3-5x faster than traditional development while maintaining professional quality and scalability for growing businesses."
    },
    {
      question: "Are NoCode solutions as secure and reliable as custom code?",
      answer: "Yes, we use enterprise-grade NoCode platforms with SOC 2 compliance, 99.9% uptime, and bank-level security. Major companies like Netflix and Airbnb use similar NoCode tools for critical business functions."
    },
    {
      question: "Can NoCode solutions handle complex business requirements?",
      answer: "Absolutely. We've built complex CRMs, e-commerce platforms, and workflow automation systems. Modern NoCode platforms can handle 90% of business requirements with better scalability than many custom solutions."
    },
    {
      question: "What happens if I outgrow the NoCode platform?",
      answer: "We design solutions with migration paths and use platforms that offer code export options. Most clients never need to migrate, but when they do, we ensure smooth transitions to custom development or enterprise platforms."
    },
    {
      question: "How do you ensure NoCode solutions integrate with existing systems?",
      answer: "Our NoCode platforms offer robust API connectivity and integration capabilities. We connect to databases, CRMs, payment processors, and custom systems through APIs, webhooks, and automated data synchronization."
    }
  ]
};